/**
 * The agent's question with tappable answers (S-525, Paper "Chat cards ·
 * spike (S-525)", C1–C2b): open, a lettered row per answer that anyone in the
 * crew can tap; answered, the question and the answer, with who and when.
 */
import { Spinner } from "@crew/ui/components/spinner";
import { cn } from "@crew/ui/lib/utils";
import { Check } from "lucide-react";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { useAnswerChoice } from "./api";
import { choiceView, letterOf } from "./choice-view";
import { shortTime } from "./format";

const SHELL = "flex w-full max-w-[520px] flex-col self-start rounded-tile bg-card shadow-ring";

export function ChoiceCard({
  messageId,
  threadId,
  data,
  me,
}: {
  messageId: string;
  threadId: string;
  data: Record<string, unknown>;
  me: Me;
}) {
  const view = choiceView(data, me.user.id);
  const answer = useAnswerChoice(threadId);
  if (view.state === "answered") {
    return (
      <div data-choice="answered" className={cn(SHELL, "gap-1.5 px-3.5 py-3")}>
        <p className="text-meta text-muted-foreground">{view.question}</p>
        <p className="flex items-center gap-2.5">
          {view.answer ? (
            <Check
              aria-hidden
              className="size-4 shrink-0 rounded-full bg-moss p-0.5 text-white"
              strokeWidth={3}
            />
          ) : null}
          <span
            className={cn(
              "min-w-0 flex-1",
              view.answer
                ? "text-body font-medium text-foreground"
                : "text-sm text-muted-foreground",
            )}
          >
            {view.answer ?? "Answered in the thread"}
          </span>
          <span className="shrink-0 text-xs text-hint">
            {view.by} · {shortTime(view.at, me.workspace.timezone)}
          </span>
        </p>
      </div>
    );
  }
  const picking = answer.isPending ? answer.variables?.option : undefined;
  return (
    <div data-choice="open" className={SHELL}>
      <p className="px-3.5 pt-3.5 pb-2.5 text-base leading-[22px] font-medium text-foreground">
        {view.question}
      </p>
      <div role="group" aria-label={view.question} className="flex flex-col">
        {view.options.map((option, i) => (
          <button
            key={option}
            type="button"
            disabled={answer.isPending}
            onClick={() => answer.mutate({ messageId, option: i })}
            className={cn(
              "flex min-h-12 items-center gap-3 border-t border-border px-3.5 py-2.5 text-left",
              "transition-colors outline-none hover:bg-accent focus-visible:bg-accent",
              "disabled:cursor-default disabled:hover:bg-transparent",
            )}
          >
            <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-muted font-mono text-xs font-medium text-muted-foreground">
              {picking === i ? <Spinner className="size-3" /> : letterOf(i)}
            </span>
            <span className="min-w-0 flex-1 text-body text-foreground">{option}</span>
          </button>
        ))}
      </div>
      <p className="border-t border-border px-3.5 pt-2.5 pb-3 text-xs text-muted-foreground">
        {answer.error ? (
          <span className="text-brick">{errorHint(answer.error)}</span>
        ) : (
          "Tap one, or reply in the thread."
        )}
      </p>
    </div>
  );
}
